import { useState, useEffect, useCallback } from "react";

/**
 * Hook to manage call timer functionality
 * @returns {Object} Call timer state and functions
 */
const useCallTimer = () => {
  const [callStartTime, setCallStartTime] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isCallActive, setIsCallActive] = useState(false);
  const [formattedTime, setFormattedTime] = useState("00:00");

  // Format duration as MM:SS
  const formatDuration = useCallback((seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  }, []);

  // Start the call timer
  const startCallTimer = useCallback(() => {
    setCallStartTime(new Date());
    setIsCallActive(true);
    setCallDuration(0);
    setFormattedTime("00:00");
  }, []);

  // Stop the call timer
  const stopCallTimer = useCallback(() => {
    setIsCallActive(false);
  }, []);

  // Reset the call timer
  const resetCallTimer = useCallback(() => {
    setCallDuration(0);
    setFormattedTime("00:00");
    setCallStartTime(new Date());
  }, []);

  // Update call duration every second
  useEffect(() => {
    let timerInterval;

    if (isCallActive && callStartTime) {
      timerInterval = setInterval(() => {
        const currentTime = new Date();
        const durationInSeconds = Math.floor((currentTime - callStartTime) / 1000);
        setCallDuration(durationInSeconds);
        setFormattedTime(formatDuration(durationInSeconds));
      }, 1000);
    }

    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [callStartTime, isCallActive, formatDuration]);

  return {
    callDuration,
    formattedTime,
    formatDuration,
    isCallActive,
    startCallTimer,
    stopCallTimer,
    resetCallTimer
  };
};

export default useCallTimer;

